// Settings: store info (GET /stores/{id}) + SEO publishing config form
// (PATCH /stores/{id}/config, 4 fields) + switchable automation mode
// (PATCH /stores/{id}/automation) with an optional pause toggle.
//
// GET /stores/{id} (StoreDetailOut) now echoes all four config fields, so the
// form prefills fully. Automation mode/paused/observation state is written via
// PATCH /automation and reconciled from its response; if GET already carries the
// current automation state we seed from it, otherwise we fall back to the
// documented defaults (review_auto / not paused).

import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  Checkbox,
  Form,
  FormLayout,
  InlineStack,
  Layout,
  Page,
  Select,
  Text,
  TextField,
  Toast,
} from "@shopify/polaris";
import { useEffect, useMemo, useState } from "react";
import { useApi } from "../api/ApiProvider";
import { useAsync, toMessage } from "../api/hooks";
import { useStore } from "../context/StoreProvider";
import { AsyncSection } from "../components/StateViews";
import { StatusBadge } from "../components/StatusBadge";
import { formatDateTime, titleCase } from "../lib/format";
import type { AutomationMode, StoreConfig } from "../api/types";

type FormFields = Record<keyof StoreConfig, string>;

const EMPTY_FORM: FormFields = {
  seo_blog_id: "",
  seo_blog_handle: "",
  seo_frontend_base_url: "",
  gsc_property: "",
};

const MODE_OPTIONS: { label: string; value: AutomationMode }[] = [
  { label: "Suggest — nothing publishes without your confirmation", value: "suggest" },
  { label: "Review, then auto (default) — publishes after review passes", value: "review_auto" },
  { label: "Autopilot — runs within your budget, frequency and risk limits", value: "autopilot" },
];

const MODE_HELP: Record<AutomationMode, string> = {
  suggest: "Everything waits for your confirmation — nothing publishes automatically.",
  review_auto: "Content publishes automatically once it clears human/rule review.",
  autopilot:
    "Runs within your budget, frequency and risk limits. New stores run a 7-day observation period under review-then-auto behaviour first.",
};

interface AutomationState {
  mode: AutomationMode;
  paused: boolean;
  observationUntil: string | null;
}

const DEFAULT_AUTOMATION: AutomationState = {
  mode: "review_auto",
  paused: false,
  observationUntil: null,
};

export function SettingsPage() {
  const api = useApi();
  const { store } = useStore();

  const detail = useAsync(() => api.getStore(store.id), [api, store.id]);

  // --- SEO config form ---
  const [form, setForm] = useState<FormFields>(EMPTY_FORM);
  const [initial, setInitial] = useState<FormFields>(EMPTY_FORM);
  const [savingConfig, setSavingConfig] = useState(false);

  // --- Automation ---
  const [automation, setAutomation] = useState<AutomationState>(DEFAULT_AUTOMATION);
  const [automationInitial, setAutomationInitial] = useState<AutomationState>(DEFAULT_AUTOMATION);
  const [savingAutomation, setSavingAutomation] = useState(false);

  const [toast, setToast] = useState<{ content: string; error?: boolean } | null>(null);

  useEffect(() => {
    if (!detail.data) return;
    const prefilled: FormFields = {
      seo_blog_id: detail.data.seo_blog_id ?? "",
      seo_blog_handle: detail.data.seo_blog_handle ?? "",
      seo_frontend_base_url: detail.data.seo_frontend_base_url ?? "",
      gsc_property: detail.data.gsc_property ?? "",
    };
    setForm(prefilled);
    setInitial(prefilled);

    const auto: AutomationState = {
      mode: detail.data.automation.mode,
      paused: detail.data.automation.is_paused,
      observationUntil: detail.data.automation.observation_until,
    };
    setAutomation(auto);
    setAutomationInitial(auto);
  }, [detail.data]);

  const configDirty = useMemo(
    () => (Object.keys(form) as (keyof FormFields)[]).some((k) => form[k] !== initial[k]),
    [form, initial],
  );
  const automationDirty = useMemo(
    () =>
      automation.mode !== automationInitial.mode ||
      automation.paused !== automationInitial.paused,
    [automation, automationInitial],
  );

  const observationActive = useMemo(() => {
    if (!automation.observationUntil) return false;
    const until = new Date(automation.observationUntil);
    return !Number.isNaN(until.getTime()) && until.getTime() > Date.now();
  }, [automation.observationUntil]);

  const update = (field: keyof FormFields) => (value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSaveConfig = async () => {
    // Only send edited fields; empty edited field → null (clear it).
    const patch: Partial<StoreConfig> = {};
    (Object.keys(form) as (keyof FormFields)[]).forEach((k) => {
      if (form[k] !== initial[k]) {
        const trimmed = form[k].trim();
        patch[k] = trimmed === "" ? null : trimmed;
      }
    });
    if (Object.keys(patch).length === 0) {
      setToast({ content: "No changes to save." });
      return;
    }
    setSavingConfig(true);
    try {
      const result = await api.updateStoreConfig(store.id, patch);
      const next: FormFields = {
        seo_blog_id: result.seo_blog_id ?? "",
        seo_blog_handle: result.seo_blog_handle ?? "",
        seo_frontend_base_url: result.seo_frontend_base_url ?? "",
        gsc_property: result.gsc_property ?? "",
      };
      setForm(next);
      setInitial(next);
      setToast({ content: "Settings saved." });
    } catch (err) {
      setToast({ content: `Couldn't save: ${toMessage(err)}`, error: true });
    } finally {
      setSavingConfig(false);
    }
  };

  const handleSaveAutomation = async () => {
    setSavingAutomation(true);
    try {
      const result = await api.updateAutomation(store.id, {
        mode: automation.mode,
        paused: automation.paused,
      });
      const next: AutomationState = {
        mode: result.mode,
        paused: result.is_paused,
        observationUntil: result.observation_until,
      };
      setAutomation(next);
      setAutomationInitial(next);
      setToast({ content: "Automation updated." });
    } catch (err) {
      setToast({ content: `Couldn't update automation: ${toMessage(err)}`, error: true });
    } finally {
      setSavingAutomation(false);
    }
  };

  return (
    <Page title="Settings" subtitle="SEO publishing configuration for this store">
      <Layout>
        <AsyncSection
          loading={detail.loading}
          error={detail.error}
          data={detail.data}
          onRetry={detail.reload}
          loadingLabel="Loading store settings"
        >
          {(data) => (
            <>
              <Layout.Section>
                <Card>
                  <BlockStack gap="200">
                    <InlineStack align="space-between" blockAlign="center">
                      <Text as="h2" variant="headingMd">
                        {data.name || data.shop_domain}
                      </Text>
                      <StatusBadge status={data.status} />
                    </InlineStack>
                    <Text as="p" tone="subdued">
                      {titleCase(data.platform)} · {data.currency} · {data.timezone}
                    </Text>
                  </BlockStack>
                </Card>
              </Layout.Section>

              <Layout.Section>
                <Card>
                  <Form onSubmit={handleSaveConfig}>
                    <BlockStack gap="400">
                      <Text as="h3" variant="headingMd">
                        Publishing configuration
                      </Text>
                      <FormLayout>
                        <TextField
                          label="SEO blog ID"
                          autoComplete="off"
                          value={form.seo_blog_id}
                          onChange={update("seo_blog_id")}
                          helpText="The Shopify blog resource that RankVine publishes articles into."
                        />
                        <TextField
                          label="SEO blog handle"
                          autoComplete="off"
                          value={form.seo_blog_handle}
                          onChange={update("seo_blog_handle")}
                          helpText="The blog's URL handle (used to build article links)."
                        />
                        <TextField
                          label="Front-end base URL"
                          type="url"
                          autoComplete="off"
                          value={form.seo_frontend_base_url}
                          onChange={update("seo_frontend_base_url")}
                          helpText="Public storefront origin, e.g. https://www.example.com (https only)."
                        />
                        <TextField
                          label="Google Search Console property"
                          autoComplete="off"
                          value={form.gsc_property}
                          onChange={update("gsc_property")}
                          helpText="The GSC property that backs opportunity discovery for this store."
                        />
                        <InlineStack align="end">
                          <Button
                            variant="primary"
                            submit
                            loading={savingConfig}
                            disabled={!configDirty || savingConfig}
                          >
                            Save
                          </Button>
                        </InlineStack>
                      </FormLayout>
                    </BlockStack>
                  </Form>
                </Card>
              </Layout.Section>

              <Layout.Section>
                <Card>
                  <BlockStack gap="400">
                    <Text as="h3" variant="headingMd">
                      Automation mode
                    </Text>
                    <Text as="p" tone="subdued">
                      Choose how much RankVine does on its own.
                    </Text>
                    {automation.paused ? (
                      <Banner tone="warning" title="Automation is paused">
                        <Text as="p">
                          RankVine won't publish anything until you resume, regardless of the
                          selected mode.
                        </Text>
                      </Banner>
                    ) : null}
                    {observationActive ? (
                      <Banner tone="info" title="Observation period active">
                        <Text as="p">
                          Until {formatDateTime(automation.observationUntil)}, autopilot still
                          requires manual approval before anything publishes.
                        </Text>
                      </Banner>
                    ) : null}
                    <FormLayout>
                      <Select
                        label="Mode"
                        options={MODE_OPTIONS}
                        value={automation.mode}
                        onChange={(value) =>
                          setAutomation((prev) => ({ ...prev, mode: value as AutomationMode }))
                        }
                        helpText={MODE_HELP[automation.mode]}
                      />
                      <Checkbox
                        label="Pause automation"
                        checked={automation.paused}
                        onChange={(checked) =>
                          setAutomation((prev) => ({ ...prev, paused: checked }))
                        }
                        helpText="Temporarily stop all automatic publishing without changing your mode."
                      />
                      <InlineStack align="end">
                        <Button
                          variant="primary"
                          loading={savingAutomation}
                          disabled={!automationDirty || savingAutomation}
                          onClick={handleSaveAutomation}
                        >
                          Save automation
                        </Button>
                      </InlineStack>
                    </FormLayout>
                    <InlineStack gap="150" blockAlign="center">
                      <Text as="span" tone="subdued" variant="bodySm">
                        Current:
                      </Text>
                      <Badge>{titleCase(automationInitial.mode)}</Badge>
                      {automationInitial.paused ? <Badge tone="warning">paused</Badge> : null}
                    </InlineStack>
                  </BlockStack>
                </Card>
              </Layout.Section>
            </>
          )}
        </AsyncSection>
      </Layout>

      {toast ? (
        <Toast content={toast.content} error={toast.error} onDismiss={() => setToast(null)} />
      ) : null}
    </Page>
  );
}
